import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { slurpGroups } from "../../lib/slurpGroupsApi";
import { SlurpLoader } from "./HolderGroupShared";
import { useConfirmDialog } from "./ConfirmDialog";

const truncWallet = (w) => (w && w.length >= 4 ? w.slice(-4) : w || "");

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - d) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

/**
 * Members modal — opened from the "N members" sticker on the group page.
 * Renders the member list with admin controls (creator: promote/demote/
 * remove · admin: remove non-admins). Removal calls /ban so the wallet
 * can't silently rejoin. Self-row is read-only.
 */
export default function MembersModal({ mint, wallet, viewerRole, minTokensToJoin = 0, symbol, onClose, onMembersChanged }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [cleaning, setCleaning] = useState(false);
  const { confirm, alert: showAlert, dialog } = useConfirmDialog();

  const refresh = useCallback(async () => {
    try {
      const r = await slurpGroups.members(mint, { limit: 200 });
      setMembers(r.members || []);
    } finally {
      setLoading(false);
    }
  }, [mint]);

  useEffect(() => { refresh(); }, [refresh]);

  const isCreator = viewerRole === "creator";
  const isAdminOrCreator = isCreator || viewerRole === "admin";

  const promote = async (target) => {
    setBusy(target);
    try {
      await slurpGroups.promote({
        mint, wallet: wallet.publicKey, signMessage: wallet.signMessage, target,
      });
      await refresh();
    } catch (e) { showAlert(e?.message || "promote failed"); }
    finally { setBusy(""); }
  };
  const demote = async (target) => {
    setBusy(target);
    try {
      await slurpGroups.demote({
        mint, wallet: wallet.publicKey, signMessage: wallet.signMessage, target,
      });
      await refresh();
    } catch (e) { showAlert(e?.message || "demote failed"); }
    finally { setBusy(""); }
  };
  const cleanupNonHolders = async () => {
    const ok = await confirm(
      `Re-check every member's on-chain balance and kick anyone who no longer holds at least the required ${symbol ? `$${symbol}` : "tokens"}. Kicked members can rejoin if they buy back in.`,
      { title: "Kick non-holders?", confirmLabel: "Kick", danger: true }
    );
    if (!ok) return;
    setCleaning(true);
    try {
      const r = await slurpGroups.cleanupNonHolders({
        mint, wallet: wallet.publicKey, signMessage: wallet.signMessage,
      });
      await refresh();
      onMembersChanged?.();
      showAlert(
        `Checked ${r.checked} members. Kicked ${r.kicked} non-holder${r.kicked === 1 ? "" : "s"}.`,
        { title: "Cleanup done" }
      );
    } catch (e) {
      showAlert(e?.message || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  const remove = async (target) => {
    const ok = await confirm("They'll be permanently banned and can't rejoin.", {
      title: "Remove member?",
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    setBusy(target);
    try {
      await slurpGroups.ban({
        mint, wallet: wallet.publicKey, signMessage: wallet.signMessage, target,
      });
      await refresh();
    } catch (e) { showAlert(e?.message || "remove failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="pm-modal__bg" onClick={onClose}>
      {dialog}
      <div className="pm-modal pm-group__members-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pm-modal__close" onClick={onClose} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M3 3 L11 11 M11 3 L3 11"/></svg>
        </button>
        <h2 className="pm-modal__title">Members ({members.length})</h2>
        {isAdminOrCreator && minTokensToJoin > 0 && (
          <div className="pm-group__members-cleanup">
            <button
              type="button"
              className="pm-group__sticker pm-group__sticker--leave"
              onClick={cleanupNonHolders}
              disabled={cleaning}
            >
              {cleaning ? "checking…" : "kick non-holders"}
            </button>
            <span className="pm-group__members-cleanup-hint">
              Re-checks on-chain balances and removes anyone below the threshold.
            </span>
          </div>
        )}
        {loading ? (
          <SlurpLoader label="Loading members" />
        ) : (
          <ul className="pm-group__members-list">
            {members.map((m) => {
              const myRow = wallet?.publicKey?.toBase58() === m.wallet;
              const canPromote = isCreator && m.role === "member" && !myRow;
              const canDemote  = isCreator && m.role === "admin"  && !myRow;
              const canRemove  = isAdminOrCreator && !myRow && m.role !== "creator" && (m.role !== "admin" || isCreator);
              const display = m.username ? `@${m.username}` : truncWallet(m.wallet);
              return (
                <li key={m.wallet} className="pm-group__members-row">
                  {/* Avatar + name area is the clickable link to the
                      member's profile. The admin action buttons stay
                      outside the link so they don't accidentally
                      navigate when an admin clicks promote/demote/remove. */}
                  <Link
                    to={`/profile/${m.wallet}`}
                    className="pm-group__members-link"
                    onClick={onClose}
                  >
                    <span className="pm-group__author-avatar" aria-hidden="true">
                      {m.image_url
                        ? <img src={m.image_url} alt="" />
                        : <span className="pm-group__author-avatar-fallback">{(m.username || m.wallet).slice(0, 2).toUpperCase()}</span>}
                    </span>
                    <div className="pm-group__members-meta">
                      <span className="pm-group__author-name">{display}</span>
                      <span className="pm-group__members-sub">
                        {m.role === "creator" && <span className="pm-group__role pm-group__role--creator">CREATOR</span>}
                        {m.role === "admin"   && <span className="pm-group__role pm-group__role--admin">ADMIN</span>}
                        <span>joined {timeAgo(m.joined_at)} ago</span>
                      </span>
                    </div>
                  </Link>
                  {(canPromote || canDemote || canRemove) && (
                    <div className="pm-group__members-actions">
                      {canPromote && (
                        <button
                          type="button"
                          className="pm-group__sticker pm-group__sticker--cream"
                          disabled={busy === m.wallet}
                          onClick={() => promote(m.wallet)}
                        >
                          make admin
                        </button>
                      )}
                      {canDemote && (
                        <button
                          type="button"
                          className="pm-group__sticker pm-group__sticker--cream"
                          disabled={busy === m.wallet}
                          onClick={() => demote(m.wallet)}
                        >
                          remove admin
                        </button>
                      )}
                      {canRemove && (
                        <button
                          type="button"
                          className="pm-group__sticker pm-group__sticker--leave"
                          disabled={busy === m.wallet}
                          onClick={() => remove(m.wallet)}
                        >
                          remove
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
